/**
 * Countdown timers.
 *
 * Fills any `.countdown-timer[data-end-date]` element's `.days`/`.hours`/
 * `.minutes`/`.seconds` spans and ticks them once a second. Ported from the
 * legacy postcardmania theme's scripts.js, where this lived globally - the
 * markup it drives came across with the popup templates but the engine did
 * not, so every countdown rendered as empty pink boxes.
 *
 * Used by the 1ktimer / holiday / current-special popup templates, in both the
 * popup body and its `#popup-recall` corner card. Kept theme-side and global
 * (rather than inside the popup plugin) because the markup is the theme's and
 * the legacy site also used it for non-popup promo banners.
 */
(function () {
    'use strict';

    var INITIALIZED_FLAG = 'pcmCountdownInitialized';

    function CountdownTimer(element) {
        this.parentElement = element;
        this.countDownDate = new Date(element.getAttribute('data-end-date')).getTime();
        this.interval = null;
        this.start();
    }

    CountdownTimer.prototype.updateTimer = function () {
        var now = new Date().getTime();
        var distance = this.countDownDate - now;

        if (distance < 0) {
            this.stop();

            // Legacy behavior: an expired timer removes itself rather than
            // sitting there showing zeroes or negative numbers.
            if (this.parentElement.parentNode) {
                this.parentElement.parentNode.removeChild(this.parentElement);
            }

            return;
        }

        var days = Math.floor(distance / (1000 * 60 * 60 * 24));
        var hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        var minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        var seconds = Math.floor((distance % (1000 * 60)) / 1000);

        this.setPart('.days', days);
        this.setPart('.hours', hours);
        this.setPart('.minutes', minutes);
        this.setPart('.seconds', seconds);

        this.updateRings({ days: days, hours: hours, minutes: minutes, seconds: seconds });
    };

    /**
     * Fills in the ring markup used by partials/countdown-timer.php - a
     * `.countdown-ring[data-unit][data-max]` per unit, each with a
     * `.countdown-ring__progress` SVG circle whose stroke-dashoffset traces
     * out value/max of the ring. No-ops for markup that doesn't have rings
     * (the popup countdowns), since querySelectorAll just returns nothing.
     */
    CountdownTimer.prototype.updateRings = function (values) {
        var rings = this.parentElement.querySelectorAll('.countdown-ring[data-unit]');

        Array.prototype.forEach.call(rings, function (ring) {
            var unit = ring.getAttribute('data-unit');
            var value = values[unit];

            if (typeof value === 'undefined') {
                return;
            }

            var progress = ring.querySelector('.countdown-ring__progress');

            if (!progress) {
                return;
            }

            var max = parseFloat(ring.getAttribute('data-max')) || 1;
            var radius = progress.r.baseVal.value;
            var circumference = 2 * Math.PI * radius;
            var fraction = Math.max(0, Math.min(1, value / max));

            progress.style.strokeDasharray = String(circumference);
            progress.style.strokeDashoffset = String(circumference * (1 - fraction));
        });
    };

    CountdownTimer.prototype.setPart = function (selector, value) {
        var node = this.parentElement.querySelector(selector);

        if (node) {
            node.textContent = String(value);
        }
    };

    CountdownTimer.prototype.stop = function () {
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = null;
        }
    };

    CountdownTimer.prototype.start = function () {
        this.updateTimer();

        // An unparseable data-end-date yields NaN, which would tick forever
        // writing "NaN" into the boxes - bail instead and leave them empty.
        if (isNaN(this.countDownDate)) {
            return;
        }

        var self = this;
        this.interval = setInterval(function () {
            self.updateTimer();
        }, 1000);
    };

    /**
     * Start every countdown on the page that isn't already running. Safe to
     * call repeatedly - popups render in wp_footer and can be revealed long
     * after DOMContentLoaded, so this is also re-run when one is shown.
     */
    function initCountdowns(root) {
        var scope = root || document;
        var elements = scope.querySelectorAll('.countdown-timer[data-end-date]');

        Array.prototype.forEach.call(elements, function (element) {
            if (element.dataset[INITIALIZED_FLAG] === '1') {
                return;
            }

            // A countdown in a popup that hasn't opened yet would tick every
            // second for the whole visit, usually for nothing. The
            // pcm_popup_shown listener below starts it when the popup opens.
            var popupWrapper = element.closest('.pcm-popup-wrapper');
            if (popupWrapper && popupWrapper.style.display === 'none') {
                return;
            }

            element.dataset[INITIALIZED_FLAG] = '1';
            new CountdownTimer(element);
        });
    }

    window.PCMCountdownTimer = { init: initCountdowns };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
            initCountdowns();
        });
    } else {
        initCountdowns();
    }

    // The popup plugin fires this when a popup is revealed; catches any
    // countdown inside markup that wasn't in the DOM at ready time.
    document.addEventListener('pcm_popup_shown', function () {
        initCountdowns();
    });

    if (window.jQuery) {
        window.jQuery(document).on('pcm_popup_shown', function () {
            initCountdowns();
        });
    }
})();
